import { useState, useEffect, CSSProperties } from 'react'

import {
  adaptOffset,
  getButtonCenter,
  getVisiblePortionCenterX
} from '../helpers'
import {
  IPosition,
  TBtnElement,
  TTooltipType,
  TTooltipPosition,
  TScrollableParentRefType
} from '../types'
import {
  DISTANCE_FROM_BTN,
  ARROW_SQUARE_WIDTH,
  ARROW_VERTICAL_TRANSFORM,
  ARROW_HORIZONTAL_TRANSFORM
} from '../consts'

const _getSquareDiagonalLength = (width: number) => {
  return Math.sqrt(2 * Math.pow(width, 2))
}

interface IPositionData {
  position: {
    x: number
    y: number
  }
  arrowPosition: CSSProperties
}

interface IViewport {
  left: number
  top: number
  right: number
  bottom: number
}

const getViewport = (): IViewport => ({
  left: 0,
  top: 0,
  right: window.innerWidth,
  bottom: window.innerHeight
})

const calculateTopPosition = (
  btnRect: DOMRect,
  tooltipRect: DOMRect,
  arrowWidth: number,
  isFloatingXTop: boolean,
  scrollableParentRef: TScrollableParentRefType,
  btnElement: TBtnElement
): IPositionData => {
  const topPosition = {
    position: {
      x: isFloatingXTop
        ? getVisiblePortionCenterX({
            scrollableParentRef,
            btnElement,
            tooltipRect
          })
        : btnRect.x - tooltipRect.width / 2 + btnRect.width / 2,
      y: btnRect.y - (tooltipRect.height + arrowWidth / 2 + DISTANCE_FROM_BTN)
    },
    arrowPosition: {
      bottom: `-${ARROW_SQUARE_WIDTH / 2}px`,
      left: '50%',
      transform: ARROW_HORIZONTAL_TRANSFORM
    }
  }
  return topPosition.position.y > 0
    ? topPosition
    : calculateBottomPosition(btnRect, tooltipRect)
}

const calculateBottomPosition = (
  btnRect: DOMRect,
  tooltipRect: DOMRect
): IPositionData => {
  const bottomPosition = {
    position: {
      x: btnRect.x - tooltipRect.width / 2 + btnRect.width / 2,
      y: btnRect.bottom + DISTANCE_FROM_BTN + ARROW_SQUARE_WIDTH
    },
    arrowPosition: {
      top: `-${ARROW_SQUARE_WIDTH / 2}px`,
      left: '50%',
      transform: ARROW_HORIZONTAL_TRANSFORM
    }
  }
  return bottomPosition.position.y < window.innerHeight
    ? bottomPosition
    : calculateRightPosition(btnRect, tooltipRect)
}

const calculateRightPosition = (
  btnRect: DOMRect,
  tooltipRect: DOMRect
): IPositionData => {
  const rightPosition = {
    position: {
      x: btnRect.right + DISTANCE_FROM_BTN + ARROW_SQUARE_WIDTH,
      y: btnRect.bottom - (tooltipRect.height / 2 + btnRect.height / 2)
    },
    arrowPosition: {
      top: '50%',
      left: `-${ARROW_SQUARE_WIDTH / 2}px`,
      transform: ARROW_VERTICAL_TRANSFORM
    }
  }
  return rightPosition.position.x < window.innerWidth
    ? rightPosition
    : calculateLeftPosition(btnRect, tooltipRect)
}

const calculateLeftPosition = (
  btnRect: DOMRect,
  tooltipRect: DOMRect
): IPositionData => {
  const leftPosition = {
    position: {
      x:
        btnRect.left -
        DISTANCE_FROM_BTN -
        ARROW_SQUARE_WIDTH -
        tooltipRect.width,
      y: btnRect.bottom - (tooltipRect.height / 2 + btnRect.height / 2)
    },
    arrowPosition: {
      top: '50%',
      right: `-${ARROW_SQUARE_WIDTH / 2}px`,
      transform: ARROW_VERTICAL_TRANSFORM
    }
  }
  return leftPosition.position.x > 0
    ? leftPosition
    : calculateBottomPosition(btnRect, tooltipRect)
}
const fitPosition = ({
  tooltipPosition,
  positionData,
  btnRect,
  tooltipRect,
  isFloatingXTop
}: {
  tooltipPosition: TTooltipPosition
  positionData: IPositionData
  btnRect: DOMRect
  tooltipRect: DOMRect
  isFloatingXTop: boolean
}): IPositionData => {
  const { position, arrowPosition } = positionData
  const viewport = getViewport()
  const buttonCenter = getButtonCenter(btnRect)

  if (tooltipPosition === 'top' || tooltipPosition === 'bottom') {
    const adaptedPositionX = Math.max(
      viewport.left - 1,
      Math.min(position.x, viewport.right - tooltipRect.width)
    )
    const adaptedArrowLeft = isFloatingXTop
      ? arrowPosition.left
      : adaptOffset(
          ((buttonCenter.x - adaptedPositionX) / tooltipRect.width) * 100
        )

    return {
      position: {
        x: adaptedPositionX,
        y: position.y
      },
      arrowPosition: {
        ...arrowPosition,
        left: `${adaptedArrowLeft}%`
      }
    }
  }

  // right position and left position

  const adaptedPositionY = Math.max(
    viewport.top + 1,
    Math.min(position.y, viewport.bottom - tooltipRect.height)
  )
  const adaptedArrowTop = isFloatingXTop
    ? arrowPosition.top
    : adaptOffset(
        ((buttonCenter.y - adaptedPositionY) / tooltipRect.height) * 100
      )

  return {
    position: {
      x: position.x,
      y: adaptedPositionY
    },
    arrowPosition: {
      ...arrowPosition,
      top: `${adaptedArrowTop}%`
    }
  }
}

export const useTooltipPositioning = ({
  tooltipPosition,
  tooltipWrapperRef,
  scrollableParentRef,
  btnElement,
  type
}: {
  tooltipPosition: TTooltipPosition
  tooltipWrapperRef: HTMLDivElement | null
  scrollableParentRef: TScrollableParentRefType
  btnElement: TBtnElement
  type: TTooltipType
}) => {
  const [position, setPosition] = useState<IPosition>({ x: 0, y: 0 })
  const [arrowStyles, setArrowStyles] = useState<CSSProperties>({})
  const arrowWidth = _getSquareDiagonalLength(ARROW_SQUARE_WIDTH)

  useEffect(() => {
    if (!btnElement || !tooltipWrapperRef) return

    const btnRect = btnElement.getBoundingClientRect()
    const tooltipRect = tooltipWrapperRef.getBoundingClientRect()
    const isFloatingXTop = type === 'floating-x-top'

    const positionCalculators: Record<TTooltipPosition, () => IPositionData> = {
      top: () =>
        calculateTopPosition(
          btnRect,
          tooltipRect,
          arrowWidth,
          isFloatingXTop,
          scrollableParentRef,
          btnElement
        ),
      right: () => calculateRightPosition(btnRect, tooltipRect),
      bottom: () => calculateBottomPosition(btnRect, tooltipRect),
      left: () => calculateLeftPosition(btnRect, tooltipRect)
    }

    const calculatePosition = () => {
      const positionData = positionCalculators[tooltipPosition]()
      return fitPosition({
        tooltipPosition,
        positionData,
        btnRect,
        tooltipRect,
        isFloatingXTop
      })
    }

    const result = calculatePosition()
    setPosition(result.position)
    setArrowStyles(result.arrowPosition)
  }, [
    tooltipPosition,
    tooltipWrapperRef,
    btnElement,
    type,
    arrowWidth,
    scrollableParentRef
  ])

  return { position, arrowStyles }
}
